import { useState, useMemo, useCallback } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine
} from 'recharts';
import { 
  Box, 
  Typography, 
  Chip,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import AlignVerticalCenterIcon from '@mui/icons-material/AlignVerticalCenter';

// 顏色映射
const colorMap = {
  '英偉達': '#4CAF50',
  '黃金': '#C9A700',
  '比特幣': '#f7931b',
  '台積電': '#f82015',
  '阿斯麥': '#9C27B0',
  'WTI原油': '#2196F3'
};

// 值轉換函數
function convertValue(name, value) {
  if (name === '黃金') return value / 31.1035;
  if (name === '比特幣') return value / 1000;
  if (['英偉達','阿斯麥','台積電'].includes(name)) return value / 10;
  return value;
}

const parseDate = (dateStr) => new Date(dateStr + 'T00:00:00');

const formatDateToString = (date) => {
  if (!date) return null;
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// 原始數據 Tooltip
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <Box sx={{
        backgroundColor: 'rgba(255,255,255,0.97)',
        border: '1px solid #ccc',
        borderRadius: '8px',
        padding: '12px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
        backdropFilter: 'blur(10px)'
      }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>{label}</Typography>
        {payload.map((entry, index) => (
          <Typography key={index} variant="body2" sx={{ color: entry.color, fontWeight: 500, mb: 0.5 }}>
            {entry.name}: {entry.value?.toFixed(4)}
            {entry.name === '黃金' && ' USD/g'}
            {entry.name === '比特幣' && ' USD/mBTC'}
            {entry.name === 'WTI原油' && ' USD/barrel'}
            {['英偉達','台積電','阿斯麥'].includes(entry.name) && ' USD/0.1Share'}
          </Typography>
        ))}
      </Box>
    );
  }
  return null;
};

// 歸一化 Tooltip
const NormalizedTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <Box sx={{
        backgroundColor: 'rgba(255,255,255,0.97)',
        border: '1px solid #ccc',
        borderRadius: '8px',
        padding: '12px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
        backdropFilter: 'blur(10px)',
        minWidth: 180
      }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>{label}</Typography>
        {payload.map((entry, index) => {
          const pct = ((entry.value - 1) * 100).toFixed(2);
          const color = entry.value >= 1 ? '#16a34a' : '#dc2626';
          return (
            <Typography key={index} variant="body2" sx={{ color: entry.color, fontWeight: 500, mb: 0.5 }}>
              {entry.name}:{' '}
              <span style={{ color }}>
                {pct >= 0 ? '+' : ''}{pct}%
              </span>
            </Typography>
          );
        })}
      </Box>
    );
  }
  return null;
};

// 自定義圖例
const CustomLegend = ({ payload }) => (
  <Box sx={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: 2, mt: 2 }}>
    {payload.map((entry, index) => (
      <Box key={index} sx={{
        display: 'flex', alignItems: 'center', gap: 1,
        px: 2, py: 1, borderRadius: '20px',
        backgroundColor: 'rgba(255,255,255,0.8)',
        border: `2px solid ${entry.color}`,
        transition: 'all 0.3s ease',
        '&:hover': { backgroundColor: 'rgba(255,255,255,1)', transform: 'translateY(-2px)', boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }
      }}>
        <Box sx={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: entry.color }} />
        <Typography variant="body2" sx={{ fontWeight: 600, color: '#333' }}>{entry.value}</Typography>
      </Box>
    ))}
  </Box>
);

const AssetChart = ({ data, filteredDates }) => {
  const [mode, setMode] = useState('raw'); // 'raw' | 'normalized'

  const { minDate, maxDate } = useMemo(() => {
    if (!filteredDates?.length) return { minDate: new Date(), maxDate: new Date() };
    return { minDate: parseDate(filteredDates[0]), maxDate: parseDate(filteredDates[filteredDates.length - 1]) };
  }, [filteredDates]);

  const [startDate, setStartDate] = useState(minDate);
  const [endDate, setEndDate] = useState(maxDate);

  // 原始圖表數據
  const chartData = useMemo(() => {
    if (!data || !filteredDates) return [];
    const grouped = data.reduce((acc, row) => {
      if (!acc[row.name]) acc[row.name] = [];
      acc[row.name].push({ date: row.date, value: convertValue(row.name, row.value) });
      return acc;
    }, {});
    return filteredDates.map(date => {
      const item = { date };
      Object.keys(grouped).forEach(name => {
        const dp = grouped[name].find(d => d.date === date);
        item[name] = dp ? dp.value : null;
      });
      return item;
    });
  }, [data, filteredDates]);

  const displayData = useMemo(() => {
    if (!chartData.length) return [];
    const s = formatDateToString(startDate);
    const e = formatDateToString(endDate);
    return chartData.filter(item => item.date >= s && item.date <= e);
  }, [chartData, startDate, endDate]);

  // 歸一化數據：以第一個有效值為基準，計算相對漲跌
  const normalizedData = useMemo(() => {
    if (!displayData.length) return [];
    const assetNames = Object.keys(displayData[0]).filter(k => k !== 'date');
    // 找每個資產的第一個有效值
    const baseValues = {};
    assetNames.forEach(name => {
      const first = displayData.find(d => d[name] != null);
      baseValues[name] = first ? first[name] : null;
    });
    return displayData.map(item => {
      const row = { date: item.date };
      assetNames.forEach(name => {
        if (item[name] != null && baseValues[name]) {
          row[name] = item[name] / baseValues[name]; // 1.0 = 基準，1.1 = +10%
        } else {
          row[name] = null;
        }
      });
      return row;
    });
  }, [displayData]);

  const assetNames = useMemo(() => {
    if (!data) return [];
    return [...new Set(data.map(d => d.name))];
  }, [data]);

  const handleStartDateChange = useCallback((e) => {
    const v = e.target.value;
    if (v && v <= formatDateToString(endDate) && v >= formatDateToString(minDate)) setStartDate(parseDate(v));
  }, [endDate, minDate]);

  const handleEndDateChange = useCallback((e) => {
    const v = e.target.value;
    if (v && v >= formatDateToString(startDate) && v <= formatDateToString(maxDate)) setEndDate(parseDate(v));
  }, [startDate, maxDate]);

  const activeData = mode === 'normalized' ? normalizedData : displayData;

  // 歸一化 Y 軸格式
  const normalizedTickFormatter = (v) => {
    const pct = ((v - 1) * 100).toFixed(0);
    return `${pct >= 0 ? '+' : ''}${pct}%`;
  };

  if (!chartData.length) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="400px">
        <Typography variant="h6" color="text.secondary">無數據可顯示</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', mt: 0 }}>
      {/* 控制列 */}
      <Stack direction="row" spacing={2} justifyContent="space-between" alignItems="center" sx={{ flexWrap: 'wrap', gap: 2, mb: 1 }}>
        {/* 左側：日期選擇器 */}
        <Stack direction="row" spacing={2} alignItems="center">
          <TextField
            label="開始日期" type="date"
            value={formatDateToString(startDate)}
            onChange={handleStartDateChange}
            size="small" sx={{ minWidth: 150 }}
            InputLabelProps={{ shrink: true }}
            inputProps={{ min: formatDateToString(minDate), max: formatDateToString(endDate) }}
          />
          <Typography variant="body2" color="text.secondary">至</Typography>
          <TextField
            label="結束日期" type="date"
            value={formatDateToString(endDate)}
            onChange={handleEndDateChange}
            size="small" sx={{ minWidth: 150 }}
            InputLabelProps={{ shrink: true }}
            inputProps={{ min: formatDateToString(startDate), max: formatDateToString(maxDate) }}
          />
          <Chip label={`${displayData.length} 天`} color="primary" size="small" sx={{ fontWeight: 600, minWidth: 60 }} />
        </Stack>

        {/* 右側：模式切換 + 範圍提示 */}
        <Stack direction="row" spacing={2} alignItems="center">
          <ToggleButtonGroup
            value={mode}
            exclusive
            onChange={(_, v) => { if (v) setMode(v); }}
            size="small"
            sx={{
              '& .MuiToggleButton-root': { px: 1.5, py: 0.5, fontSize: '0.78rem', fontWeight: 600, textTransform: 'none' },
              '& .Mui-selected': { backgroundColor: '#1976d2 !important', color: '#fff !important' },
            }}
          >
            <ToggleButton value="raw">
              <ShowChartIcon sx={{ fontSize: 16, mr: 0.5 }} />
              原始價格
            </ToggleButton>
            <ToggleButton value="normalized">
              <AlignVerticalCenterIcon sx={{ fontSize: 16, mr: 0.5 }} />
              歸一化
            </ToggleButton>
          </ToggleButtonGroup>

          <Box sx={{ textAlign: 'right' }}>
            <Typography variant="caption" color="text.secondary">
              數據範圍：{formatDateToString(minDate)} ~ {formatDateToString(maxDate)}
            </Typography>
          </Box>
        </Stack>
      </Stack>

      {/* 歸一化說明 */}
      {mode === 'normalized' && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, pl: 0.5 }}>
          以各指標在當前時間段內的第一個數據點為基準（0%），反映相對漲跌幅
        </Typography>
      )}

      {/* 主圖表 */}
      <Box sx={{ height: 600, width: '100%' }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={activeData} margin={{ top: 30, right: 30, left: 20, bottom: 60 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" strokeOpacity={0.7} />
            <XAxis
              dataKey="date"
              tick={{ fontSize: 12 }}
              angle={-45}
              textAnchor="end"
              height={60}
              stroke="#666"
              interval="preserveStartEnd"
              tickFormatter={(v) => displayData.length > 100 ? v.slice(5, 10) : v.slice(2, 10)}
            />
            <YAxis
              tick={{ fontSize: 12 }}
              stroke="#666"
              tickFormatter={mode === 'normalized' ? normalizedTickFormatter : undefined}
              label={mode === 'raw' ? {
                value: '價格 (USD)',
                angle: -90,
                position: 'insideLeft',
                style: { textAnchor: 'middle', fill: '#666' }
              } : {
                value: '相對基準漲跌幅',
                angle: -90,
                position: 'insideLeft',
                style: { textAnchor: 'middle', fill: '#666' }
              }}
            />
            {/* 歸一化模式下顯示基準線 */}
            {mode === 'normalized' && (
              <ReferenceLine y={1} stroke="#999" strokeDasharray="4 4" label={{ value: '基準 0%', position: 'right', fontSize: 11, fill: '#999' }} />
            )}
            <Tooltip content={mode === 'normalized' ? <NormalizedTooltip /> : <CustomTooltip />} />
            <Legend content={<CustomLegend />} />
            {assetNames.map(name => (
              <Line
                key={name}
                type="monotone"
                dataKey={name}
                stroke={colorMap[name] || '#000'}
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 4, stroke: colorMap[name] || '#000', strokeWidth: 2, fill: '#fff' }}
                connectNulls={false}
                isAnimationActive={false}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </Box>
    </Box>
  );
};

export default AssetChart;